// WP13 closure — Evidence Explorer rebound to the ACTIVE authoritative
// package via GET /api/wp12/evidence. Evidence is supporting source material:
// render → search → filter → navigate. No derived analytics, no generated
// summaries, refs resolved only by canonical publisher IDs.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useWp12 } from "./useWp12";
import InfoTip from "./InfoTip";

const PAGE = 50;

const Select = ({ value, onChange, options, all, testId }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="text-[11px] px-2 py-1.5 rounded border border-[hsl(var(--line))] bg-[hsl(var(--surface))] max-w-[210px]">
        <option value="">{all}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

const EvidenceRow = ({ e }) => (
    <details className="card-flat" data-testid={`wp13-evidence-row-${e.evidenceId}`}>
        <summary className="px-4 py-3 cursor-pointer select-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-[11.5px] font-medium leading-snug min-w-0 truncate">{e.displayTitle || e.sourceDomain}</span>
                <span className="num text-[9px] text-[hsl(var(--mute))] flex-shrink-0">{e.evidenceDate}</span>
            </div>
            <div className="text-[9px] text-[hsl(var(--mute))] mt-0.5">
                {e.sourceDomain}{e.newsEventType ? ` · ${e.newsEventType}` : ""}{e.primaryUseCaseLabel ? ` · ${e.primaryUseCaseLabel}` : ""}
            </div>
        </summary>
        <div className="px-4 pb-3.5 pt-1 border-t border-[hsl(var(--line))] space-y-2">
            {e.summary && <p className="text-[11px] leading-relaxed text-[hsl(var(--ink-soft))]">{e.summary}</p>}
            {(e.patterns || []).length > 0 && (
                <div>
                    <div className="text-[8.5px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1">Supports patterns</div>
                    <div className="flex flex-wrap gap-1.5">
                        {e.patterns.map((p) => (
                            <Link key={p.canonicalPatternId} to={`/market-v2/patterns/${encodeURIComponent(p.canonicalPatternId)}`}
                                  className="text-[10px] px-2 py-0.5 rounded-full border border-[hsl(var(--line))] hover:text-[hsl(var(--accent-ink))] hover:border-[hsl(var(--accent))]"
                                  data-testid={`wp13-evidence-pattern-${p.canonicalPatternId}`}>
                                {p.title}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
            {(e.organizations || []).length > 0 && (
                <div>
                    <div className="text-[8.5px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1">Companies mentioned</div>
                    <div className="flex flex-wrap gap-1.5">
                        {e.organizations.map((o) => (
                            <Link key={o.canonicalOrganizationId} to={`/market-v2/organizations/${encodeURIComponent(o.canonicalOrganizationId)}`}
                                  className="text-[10px] px-2 py-0.5 rounded-full border border-[hsl(var(--line))] hover:text-[hsl(var(--accent-ink))] hover:border-[hsl(var(--accent))]"
                                  data-testid={`wp13-evidence-org-${o.canonicalOrganizationId}`}>
                                {o.displayName}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
            <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[9px] text-[hsl(var(--mute))]">
                {(e.blockchains || []).length > 0 && <span>Blockchains: {e.blockchains.join(", ")}</span>}
                {(e.assetClasses || []).length > 0 && <span>Asset classes: {e.assetClasses.join(", ")}</span>}
                {(e.locations || []).length > 0 && <span>Locations: {e.locations.join(", ")}</span>}
            </div>
            {e.sourceUrl && (
                <a href={e.sourceUrl} target="_blank" rel="noopener noreferrer"
                   className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline inline-block"
                   data-testid={`wp13-evidence-source-${e.evidenceId}`}>
                    Open source →
                </a>
            )}
        </div>
    </details>
);

export default function EvidenceExplorer() {
    const marketRes = useWp12("market");
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [zone, setZone] = useState("");
    const [useCase, setUseCase] = useState("");
    const [eventType, setEventType] = useState("");
    const [week, setWeek] = useState("");
    const [skip, setSkip] = useState(0);
    const timer = useRef(null);

    useEffect(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => { setDebounced(search); setSkip(0); }, 300);
        return () => clearTimeout(timer.current);
    }, [search]);

    const params = new URLSearchParams({ search: debounced, zone, use_case: useCase, event_type: eventType, week, skip: String(skip), limit: String(PAGE) });
    const { status, data, error } = useWp12(`evidence?${params.toString()}`);

    const zones = marketRes.data?.marketZones || [];
    const useCases = marketRes.data?.useCases || [];
    const rows = data?.rows || [];
    const total = data?.total ?? 0;
    const facets = data?.facets || {};
    const reset = (fn) => (v) => { fn(v); setSkip(0); };

    return (
        <div className="max-w-4xl space-y-4" data-testid="wp13-evidence-explorer">
            <div>
                <h1 className="text-xl font-semibold">Evidence</h1>
                <p className="text-[11.5px] text-[hsl(var(--mute))] mt-0.5 inline-flex items-center gap-1.5">
                    <span data-testid="wp13-evidence-grandtotal">{data?.grandTotal ?? "…"}</span> published evidence records in the active publication.
                    <InfoTip label="Evidence" testId="wp13-tip-evidence-explorer">
                        Published supporting source material rendered verbatim from the active publication. Evidence volume reflects observation coverage — it is never a strength, trend or importance measure.
                    </InfoTip>
                </p>
            </div>

            <div className="card-flat p-3 flex items-center gap-2 flex-wrap" data-testid="wp13-evidence-filters">
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Search evidence…" data-testid="wp13-evidence-search"
                       className="text-[11px] px-2.5 py-1.5 rounded border border-[hsl(var(--line))] bg-[hsl(var(--surface))] w-52" />
                <Select value={zone} onChange={reset(setZone)} options={zones.map((z) => ({ value: z.scopeKey, label: z.label }))} all="All market zones" testId="wp13-evidence-filter-zone" />
                <Select value={useCase} onChange={reset(setUseCase)} options={useCases.map((u) => ({ value: u.scopeKey, label: u.label }))} all="All use cases" testId="wp13-evidence-filter-usecase" />
                <Select value={eventType} onChange={reset(setEventType)} options={(facets.eventTypes || []).map((t) => ({ value: t, label: t }))} all="All event types" testId="wp13-evidence-filter-event" />
                <Select value={week} onChange={reset(setWeek)} options={(facets.weeks || []).map((w) => ({ value: w, label: w }))} all="All weeks" testId="wp13-evidence-filter-week" />
                <span className="text-[10px] text-[hsl(var(--mute))] ml-auto num" data-testid="wp13-evidence-count">{total} matching</span>
            </div>

            {status === "loading" && <div className="text-[11px] text-[hsl(var(--mute))] p-4" data-testid="wp13-evidence-loading">Loading…</div>}
            {status === "error" && <div className="text-[11px] p-4" data-testid="wp13-evidence-error">Evidence could not be loaded{error ? ` (${error})` : ""}.</div>}

            {status === "ready" && rows.length === 0 && (
                <div className="card-flat p-6 text-center text-[12px] text-[hsl(var(--ink-soft))]" data-testid="wp13-evidence-empty">
                    No published evidence matches these filters.
                </div>
            )}

            <div className="space-y-2" data-testid="wp13-evidence-list">
                {rows.map((e) => <EvidenceRow key={e.evidenceId} e={e} />)}
            </div>

            {total > PAGE && (
                <div className="flex items-center gap-3" data-testid="wp13-evidence-pagination">
                    <button type="button" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}
                            className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline disabled:opacity-30 disabled:no-underline"
                            data-testid="wp13-evidence-prev">← Previous</button>
                    <span className="text-[10px] text-[hsl(var(--mute))] num">{skip + 1}–{Math.min(skip + PAGE, total)} of {total}</span>
                    <button type="button" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}
                            className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline disabled:opacity-30 disabled:no-underline"
                            data-testid="wp13-evidence-next">Next →</button>
                </div>
            )}
        </div>
    );
}
